import React, { useCallback, useState } from 'react';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Link, useHistory } from 'react-router-dom';
import Form, { PasswordField, Submit } from '../../containers/form';
import Auth from '..';
import { StyledCardActions } from './styled';
import style from './style.module.scss';
import { useQueryString } from '../hooks/useQueryString';
import LoginBaseContainer from './LoginBaseContainer';
import PasswordInstructions, {
  getPasswordValidations,
  passwordRegExPattern,
  passwordRules,
} from 'shared/no-auth/components/AccountEditor/PasswordInstructions';
import BoxedMessage from './BoxedMessage';

const initialValues = {};

export default function GotResetCode() {
  const { push } = useHistory();
  const query = useQueryString();
  const [email] = useState<string>(
    decodeURIComponent(
      (query.get('email') || query.get('username') || '').replace(/ /g, '+'),
    ),
  );
  const [code] = useState<string>(query.get('code') || '');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [validations, setValidations] = useState<boolean[]>([]);
  const [isExpiredCode, setIsExpiredCode] = useState<boolean>(!code || !email);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const handlePassword = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setPassword(value);
      setValidations(getPasswordValidations(value));
    },
    [setPassword, setValidations],
  );
  const handleConfirmPassword = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setConfirmPassword(value);
    },
    [setConfirmPassword],
  );

  const onSubmit = useCallback(async () => {
    setError('');
    setSuccess('');

    if (validations.length === 0 || validations.includes(false)) {
      setError('Password is required');
      return false;
    }
    if (password !== confirmPassword) {
      setError('Password and Confirm Password fields must match.');
      return false;
    }
    try {
      await Auth.forgotPasswordSubmit(email, code, password);
      setSuccess('Password reset successfully!');
      setTimeout(() => {
        push(email ? `/login?email=${email}` : '/login');
      }, 2000);
    } catch (e) {
      if (
        e.code === 'ExpiredCodeException' ||
        e.name === 'ExpiredCodeException'
      ) {
        setIsExpiredCode(true);
      }
      setError(e.message || e);
      return false;
    }
  }, [email, code, password, confirmPassword, setSuccess, setError]);

  if (success) {
    return (
      <LoginBaseContainer>
        <BoxedMessage
          message={`Successfully set the password for ${email}. Redirecting to the login page where you can sign-in with the new password.`}
        />
      </LoginBaseContainer>
    );
  }

  if (isExpiredCode) {
    return (
      <LoginBaseContainer>
        <BoxedMessage
          isError={true}
          message={
            <>
              It seems that the reset link is expired, please try resetting your
              password again.
              <br />
              <br />
              <Link to="/forgot">Reset your password</Link>
            </>
          }
        />
      </LoginBaseContainer>
    );
  }

  return (
    <LoginBaseContainer>
      <div className={style.label__wrapper}>
        <h1>Enter Password Reset code</h1>
        <div>{`for ${email}`}</div>
      </div>
      <Form
        autoComplete="off"
        initialValues={initialValues}
        onSubmit={onSubmit}
        onError={undefined}
        onChange={undefined}
        onDirty={undefined}
        onValidate={undefined}
      >
        <label htmlFor="password" className="form__label">
          Password
        </label>
        <PasswordField
          name="password"
          id="password"
          label="New Password"
          placeholder="New Password"
          fullWidth
          required
          value={password}
          onChange={handlePassword}
          pattern={passwordRegExPattern}
          passwordrules={passwordRules}
          minlength={12}
          maxlength={60}
        />
        <br />
        <br />
        <label htmlFor="password2" className="form__label">
          Confirm Password
        </label>
        <PasswordField
          name="password2"
          id="password2"
          label="Confirm New Password"
          placeholder="Confirm New Password"
          fullWidth
          required
          value={confirmPassword}
          onChange={handleConfirmPassword}
          pattern={passwordRegExPattern}
          passwordrules={passwordRules}
          minlength={12}
          maxlength={60}
        />
        <br />
        <br />
        <PasswordInstructions
          password={password}
          confirmPassword={confirmPassword}
          passwordValidations={validations}
        />
        <br />
        <br />
        <Typography component="div" variant="body2" color="textSecondary">
          Back to <Link to="/login">Login</Link>
        </Typography>
        <br></br>
        {error && (
          <Chip
            className="error"
            icon={<ErrorOutlineIcon />}
            label={error}
            variant="outlined"
          />
        )}
        <StyledCardActions>
          <Submit variant="contained" color="primary" alwaysEnabled={undefined}>
            Set New Password
          </Submit>
        </StyledCardActions>
      </Form>
    </LoginBaseContainer>
  );
}
